import { inject, Injectable } from '@angular/core';
import { PerformUrl } from '@constants/api-url.constant';
import {
    attachmentNotUploaded,
    attDownSyncDto,
    attUploadURLUpdate,
    clearDeletedRecordsQuery,
    getDeletedRecordsQuery,
    dirtyRecords,
    resetSyncAction,
    resetSyncStatusOnFailed,
    setSyncStatusInProgress,
    updateAttStatus,
    updateDownSyncAtt,
    updateGeocodeSeq,
    updateContactSeq,
    getNullSeqContacts,
    updateContactSqs,
    DownSyncAttPayloadQuery,
    GET_AUDIT_LIST,
    UpSyncAttQuery,
} from '@constants/query.constant';
import { API_BASE_URL, Environment } from '@environment/environment';
import { GenericError } from '@models/classes/generic.error';
import { AttUploadUrlResponse, DownSyncRequestDto, DownSyncResponseDto } from '@models/dto/perform-sync.dto';
import { ErrorCode } from '@models/enums/error.codes';
import { AjaxResponse } from '@models/types/ajax-response.type';
import { AttachmentNotUploaded, AttDownloadUrlReqDto, AttDownloadUrlResDto, DeleteRecords, DeleteRecordUpSync, DeleteRecordUpSyncTableData, PerformContact, PerformSyncResult, UpSyncVisitReq, VisitStatus } from '@models/types/perform-types/perform-visit.types';
import { FileSystemService } from '@services/file-system/file-system.service';
import { HttpService } from '@services/http/http.service';
import { SqliteService } from '@services/sqlite/sqlite.service';
import { MsalAuthService } from '@src/msal/msal-auth.service';
import { VisitDbSyncTaskStatus, VisitUpSyncInfoTable } from '@services/perform/perform-idb/backbone-idb.types';
import { PerformIdbService } from '@services/perform/perform-idb/perform-idb.service';
import { SyncAction } from '@models/enums/db-query-action.enum';
import { Log, LogService } from '@services/log/log.service';
import { AttachmentCloutUploadStatus } from '@models/enums/attachment-cloud-upload-status.enum';
// import { PerformSyncNotifierService } from '../perform-sync-notifier/perform-sync-notifier.service';
import { StorageService } from '@services/storage/storage.service';
import { SYNC_IN_PROGRESS, CONTACTS_FAILED_VISIT, GEOCODE_FAILED_VISIT } from '@services/storage/storage.keys.constant';
import { AppSettingsService } from '@services/app-settings/app-settings.service';
import { PerformAuditAttachmentLocation } from '@models/enums/perform-attachment-location.enum';
import { HttpError } from '@models/classes/http-error.model';
import { ErrorBase } from '@models/classes/error-base.model';
import { VisitListService } from '@services/visit-list/visit-list.service';
import { UtilService } from '@services/util/util.service';
@Injectable({
    providedIn: 'root',
})
export class PerformSyncService {
    readonly #httpSvc: HttpService = inject(HttpService);
    readonly #sqliteSvc: SqliteService = inject(SqliteService);
    readonly #loggerSvc: LogService = inject(LogService);
    readonly #msalAuthSvc: MsalAuthService = inject(MsalAuthService);
    readonly #performIdbSvc: PerformIdbService = inject(PerformIdbService);
    readonly #storageSvc: StorageService = inject(StorageService);
    readonly #fileSystemSvc: FileSystemService = inject(FileSystemService);
    readonly #visitListSvc: VisitListService = inject(VisitListService);
    readonly #appSettingSvc: AppSettingsService = inject(AppSettingsService);
    readonly #utilSvc: UtilService = inject(UtilService);



    // readonly #performSyncNotify: PerformSyncNotifierService = inject(PerformSyncNotifierService)

    constructor() {
        this.resetDownloadingStatus();
    }


    //#region  
    private async resetDownloadingStatus() {
        const userName = this.#msalAuthSvc.activeUserName;
        if (!userName) {
            throw new GenericError({
                message: 'userName not found',
                errorCode: ErrorCode.NULL_OR_EMPTY,
                location: 'perform-sync-.service>autoSync()',
            });
        }

        const visits = await this.#performIdbSvc.getBackboneVisits(userName);
        const downloadingVisits = visits.filter((visit) => visit.syncStatus == VisitDbSyncTaskStatus.Downloading);
        if (downloadingVisits.length == 0) {
            return;
        }
        // downloading
        for await (const visit of downloadingVisits) {
            const visitDbExists: boolean = await this.#sqliteSvc.dbExists(visit.visit_no + '.db');
            if (visitDbExists) {
                await this.#sqliteSvc.deleteDB(visit.visit_no + '.db', 'sync', true);
            }
            this.#fileSystemSvc.clearVisitAttachments(visit.visit_no);
            this.#performIdbSvc.updateVisitSyncStatus(visit.seq, new Date(), VisitDbSyncTaskStatus.DownloadFailed);
        }

        // in queue for doqnloading
        const inDownloadingQueque = visits.filter((visit) => visit.syncStatus == VisitDbSyncTaskStatus.InDownLoadQueue);
        for await (const inQueueVisit of inDownloadingQueque) {
            await this.#performIdbSvc.updateVisitSyncStatus(inQueueVisit.seq, new Date(), VisitDbSyncTaskStatus.NotDownloaded);
        }

        // uploading visits
        const uploading = visits.filter((visit) => visit.syncStatus == VisitDbSyncTaskStatus.Uploading);
        for await (const upVisit of uploading) {
            await this.#performIdbSvc.updateVisitSyncStatus(upVisit.seq, new Date(), VisitDbSyncTaskStatus.UploadFailed);
        }

        // in queue for uploading
        const inuploadingQueque = visits.filter((visit) => visit.syncStatus == VisitDbSyncTaskStatus.InUpLoadQueue);
        for await (const inUpVisit of inuploadingQueque) {
            await this.#performIdbSvc.updateVisitSyncStatus(inUpVisit.seq, new Date(), VisitDbSyncTaskStatus.None);
        }
    }
    //#endregion


    //#region up sync
    async upSyncAttachments(visitNo: string, targetLocation: PerformAuditAttachmentLocation, auditNo?: string) {

        let upSyncFailedAttMap: GenericError<any>[] = []

        if ((await this.#sqliteSvc.connectedDBName('sync')) !== visitNo + '.db') {
            await this.#sqliteSvc.connectDB(visitNo + '.db', 'sync');
        }
        const fetchAttQuery = UpSyncAttQuery(visitNo, targetLocation, auditNo);

        const imagesToUpSync = await this.#sqliteSvc.runQuery<AttachmentNotUploaded[]>(fetchAttQuery, 'sync');
        if (imagesToUpSync.length == 0) {
            return;
        }
        const uploadUrls: AttUploadUrlResponse[] = await this.#fetchUploadUrl(imagesToUpSync);
        const user = this.#msalAuthSvc.activeUserName;
        if (user == null) {
            throw new GenericError({
                message: 'Active user not found',
                location: 'perform-sync-service>updateAttchmentTableUrls()',
                errorCode: ErrorCode.NULL_OR_EMPTY,
            });
        }
        for (let att of uploadUrls) {
            const query = attUploadURLUpdate(att, user);
            await this.#sqliteSvc.runQuery(query, 'sync');
            await this.#uploadAtt(att, targetLocation, auditNo).catch(async (err) => {
                // this.#loggerSvc.add({
                //     msg: 'error in PerformSyncService>uploadAtt()',
                //     object: err,
                //     date: new Date().toISOString(),
                //     type: 'error',
                // });
                const _query = updateAttStatus(att.seq, user, AttachmentCloutUploadStatus.Failed);
                await this.#sqliteSvc.runQuery(_query, 'sync');
                upSyncFailedAttMap.push(new GenericError({
                    errorCode: ErrorCode.ATT_UP_SYNC_FAILED,
                    message: 'attachment up-sync failed',
                    errorObject: err,
                    location: 'perform-sync.service>upSyncAttachments()'
                }));
            });

            const updateUpSyncAttQuery = updateAttStatus(att.seq, user, AttachmentCloutUploadStatus.Uploaded, att.attachmentPath);
            await this.#sqliteSvc.runQuery(updateUpSyncAttQuery, 'sync');

        }
        if (upSyncFailedAttMap.length > 0) {
            throw new GenericError({
                errorCode: ErrorCode.ATT_UP_SYNC_FAILED,
                message: 'one or more attachment up-sync failed',
                errorObject: upSyncFailedAttMap,
                location: 'perform-sync.service > upSyncAttachments()'
            });
        }
    }

    async #uploadAtt(att: AttUploadUrlResponse, targetLocation: PerformAuditAttachmentLocation, auditNo?: string) {
        let attFileMap: Map<string, File>;
        if (auditNo) {
            attFileMap = await this.#fileSystemSvc.getVisitAttachments(att.visitNo, [att.fileName], targetLocation, auditNo);
        } else {
            attFileMap = await this.#fileSystemSvc.getVisitAttachments(att.visitNo, [att.fileName], targetLocation);
        }
        const file = attFileMap.get(att.fileName);
        await this.#httpSvc.put([att.uploadUrl, file, { headers: { 'x-ms-blob-type': 'BlockBlob' } }], true)
    }
    async #fetchUploadUrl(payload: AttachmentNotUploaded[]): Promise<AttUploadUrlResponse[]> {
        const url = Environment.baseUrl + PerformUrl.ATTACHMENT_UPLOAD;
        const response = await this.#httpSvc.post<AjaxResponse<AttUploadUrlResponse[]>>([url, payload], true);
        return response.data;
    }

    async #upSyncVisit(visitNo: string): Promise<void> {
        const userName = this.#msalAuthSvc.activeUserName;
        if (userName == null) {
            throw new GenericError({
                message: 'Active user not found',
                location: 'perform-sync-service>updateAttchmentTableUrls()',
                errorCode: ErrorCode.NULL_OR_EMPTY,
            });
        }
        await this.#upSyncDeleteRecords(userName, visitNo);
        let tables: VisitUpSyncInfoTable[] = [];
        tables = (await this.#performIdbSvc.getUpSyncVisitInfo(visitNo, userName)).filter(
            (table) => table.upSync)
        tables.sort((tableA, tableB) => {
            if (tableA.tableName === 'audit_sections') {
                return -1;
            } else {
                return 0;
            }
        })

        const specialTables = new Set<string>()

        const syncActions = [SyncAction.Insert, SyncAction.Update];
        for (const action of syncActions) {
            const specialTablesDownSync = await this.#upSyncVisitTableDirtyRecords(tables, userName, visitNo, action);
            specialTablesDownSync.forEach((table) => {
                specialTables.add(table)
            })
        }
        await this.#resetSyncActionCol(tables);
        if (specialTables.has('contacts')) {
            await this.downSyncContacts(visitNo)
        }
        if (specialTables.has('geocode')) {
            await this.downSyncGeoCode(visitNo)
        }
        
    }

    async #upSyncDeleteRecords(userName: string, visitNo: string) {
        const deleteQuery = getDeletedRecordsQuery()
        const deletedRecords = await this.#sqliteSvc.runQuery<DeleteRecords[]>(deleteQuery, 'sync');
        if (deletedRecords.length == 0) {
            return;
        }
        const recordsMap = new Map<string, DeleteRecordUpSyncTableData[]>();

        for (const record of deletedRecords) {
            const tableName = record.table_name;
            if (!recordsMap.has(tableName)) {
                recordsMap.set(tableName, []);
            }
            let _record: DeleteRecordUpSyncTableData = {
                sync_action: record.action,
                sync_status: record.sync_status,
                [record.id_name]: record.id_value
            }
            recordsMap.get(tableName)?.push(_record);
        }

        let tables: DeleteRecordUpSync[] = []




        for (const [tableName, records] of recordsMap.entries()) {
            tables.push({
                tableName,
                data: records,
            });
        }

        const upSyncPayload = {
            deviceId: '123',
            requestId: '12345',
            user: userName,
            visitNo,
            tables
        };



        const baseUrl = Environment.baseUrl + PerformUrl.UPLOAD_VISIT_DATA;
        await this.#httpSvc.post<AjaxResponse<any>>([baseUrl, upSyncPayload], true);
        const clearDeleteRecordsTableQuery = clearDeletedRecordsQuery();
        await this.#sqliteSvc.runQuery(clearDeleteRecordsTableQuery, 'sync');
    }

    async #resetSyncActionCol(tables: VisitUpSyncInfoTable[]) {
        for (const table of tables) {
            const resetQuery = resetSyncAction(table.tableName);
            await this.#sqliteSvc.runQuery(resetQuery, 'sync');
        }
    }

    async #upSyncVisitTableDirtyRecords(tables: VisitUpSyncInfoTable[], userName: string, visitNo: string, action: SyncAction) {
        const upSyncPayload: UpSyncVisitReq = {
            deviceId: '123',
            requestId: '12345',
            user: userName,
            visitNo,
            tables: [],
        };

        const contactAndGeoCodeTables = [];

        for (const table of tables) {
            try {

                const updateQuery = setSyncStatusInProgress(table.tableName, action);
                await this.#sqliteSvc.runQuery(updateQuery, 'sync')

                const actionQuery = dirtyRecords(table.tableName, action);
                const records = await this.#sqliteSvc.runQuery<any>(actionQuery, 'sync');

                if (records?.length > 0) {
                    upSyncPayload.tables.push({
                        data: records,
                        tableName: table.tableName,
                    });
                    if (table.tableName == 'contacts' || table.tableName == 'geocode') {
                        contactAndGeoCodeTables.push(table.tableName)
                    }
                }
            } catch (err) {
                this.#loggerSvc.add({
                    date: new Date().toISOString(),
                    msg: `err in perform-sync.service>upSyncVisitData() table name ${table.tableName} visit : ${table.visitNo}`,
                    object: err,
                    type: 'error',
                })
                const updateQuery = resetSyncStatusOnFailed(table.tableName, action);
                await this.#sqliteSvc.runQuery(updateQuery, 'sync')
            }
        }
        if (upSyncPayload.tables.length > 0) {
            const url = API_BASE_URL + PerformUrl.UPLOAD_VISIT_DATA;
            await this.#httpSvc.post([url, upSyncPayload], true);
        }
        return contactAndGeoCodeTables;
    }


    #getDownSyncAppSettingKey(tableName: string, visitNo: string) {
        return `${visitNo}_${tableName}`
    }

    async #startUpSync(visitNo: string) {
        const dbName = visitNo + '.db';
        if (!(await this.#sqliteSvc.dbExists(dbName))) {
            return;
        }
        await this.#sqliteSvc.closeConnection('sync');
        await this.#sqliteSvc.connectDB(dbName, 'sync');
        //#region sync (up then down) two special tables  `geocode` and `contacts`


        const geocodeUpSyncFailed = await this.#appSettingSvc.getString(this.#getDownSyncAppSettingKey(GEOCODE_FAILED_VISIT, visitNo));
        if (geocodeUpSyncFailed == 'true') {
            await this.downSyncGeoCode(visitNo)
        }

        const contactUpSyncFailed = await this.#appSettingSvc.getString(this.#getDownSyncAppSettingKey(CONTACTS_FAILED_VISIT, visitNo));
        if (contactUpSyncFailed == 'true') {
            await this.downSyncContacts(visitNo)
        }
        //#endregion

        await this.#upSynAllVisitAttachments(visitNo);
        await this.#upSyncVisit(visitNo);

    }


    async #upSynAllVisitAttachments(visitNo: string) {
        let upSyncFailedAtt: GenericError<any>[] = []
        const auditQuery = GET_AUDIT_LIST(visitNo);
        const audits = await this.#sqliteSvc.runQuery<{ audit_no: string }[]>(auditQuery, 'sync');
        const attachmentLocations: PerformAuditAttachmentLocation[] = [
            PerformAuditAttachmentLocation.Audit,
            PerformAuditAttachmentLocation.Attempted,
            PerformAuditAttachmentLocation.Questions
        ];

        let attErrHandler = (err: any) => {
            if (err instanceof GenericError && err.errorCode == ErrorCode.ATT_UP_SYNC_FAILED) {
                err.errorObject.forEach((err: GenericError<any>) => {
                    upSyncFailedAtt.push(err);
                })
            } else {
                throw err;
            }
        }

        for await (const audit of audits) {
            for await (const location of attachmentLocations) {
                await this.upSyncAttachments(visitNo, location, audit.audit_no).catch(err => attErrHandler);
            }
        }
        // upload attachments of signature
        await this.upSyncAttachments(visitNo, PerformAuditAttachmentLocation.SubmitFormSignature).catch(err => attErrHandler)

        if (upSyncFailedAtt.length > 0) {
            throw new GenericError({
                errorCode: ErrorCode.ATT_UP_SYNC_FAILED,
                message: 'one or more attachment up sync failed',
                errorObject: upSyncFailedAtt,
                location: `perform-sync-service > upSynAllVisitAttachments()`
            })
        }

    }

    async #updateLastSyncTime(visitNo: string, userName: string, syncStatus: VisitDbSyncTaskStatus, lastSyncFailedReason?: string): Promise<number>;
    async #updateLastSyncTime(visitNo: string[], userName: string, syncStatus: VisitDbSyncTaskStatus, lastSyncFailedReason?: string): Promise<number>;
    async #updateLastSyncTime(visitNo: string | string[], userName: string, syncStatus: VisitDbSyncTaskStatus, lastSyncFailedReason?: string): Promise<number> {

        if (typeof visitNo === 'string') {
            const seq = visitNo + userName;
            return await this.#performIdbSvc.updateVisitSyncStatus(seq, new Date(), syncStatus, lastSyncFailedReason)
        } else {
            const sequences = visitNo.map((seq) => seq + userName);
            return await this.#performIdbSvc.updateVisitSyncStatus(sequences, new Date(), syncStatus, lastSyncFailedReason)
        }
    }

    //#endregion

    //#region down sync


    /**
     * 
     * @param visitNo Visit number
     * @param tables List of tables to download. If not provided, all tables will be downloaded.
     * @returns DownSyncResponseDto
     * @throws GenericError if userName is not found
     */
    async downloadVisitTables(visitNo: string): Promise<DownSyncResponseDto>;
    async downloadVisitTables(visitNo: string, tables: string[]): Promise<DownSyncResponseDto>;
    async downloadVisitTables(visitNo: string, tables?: string[]): Promise<DownSyncResponseDto> {
        const payload: DownSyncRequestDto = {
            visitNo: visitNo,
            requestId: '12345',
        };

        if (tables) {
            payload.tables = tables;
        }

        const url = Environment.baseUrl + PerformUrl.downSync;
        const response = await this.#httpSvc.post<AjaxResponse<DownSyncResponseDto>>([url, payload], true);
        const visitdData: DownSyncResponseDto = response.data;
        const userName = this.#msalAuthSvc.activeUserName;
        if (!userName) {
            throw new GenericError({
                message: 'userName not found',
                errorCode: ErrorCode.NULL_OR_EMPTY,
                location: 'perform-sync-.service>downloadVisit()',
            });
        }
        const upSyncDataInfo: VisitUpSyncInfoTable[] = visitdData.tables.map((table) => {
            const seq = userName + visitNo + table.tableName;
            const visitData: VisitUpSyncInfoTable = {
                orderNum: table.orderNum,
                tableName: table.tableName,
                upSync: table.upSync,
                visitNo,
                userName,
                seq
            };
            return visitData;
        });
        await this.#saveupSyncInfo(upSyncDataInfo);
        return response.data;
    }


    #createInsertQuery(tableName: string, columns: string[], data: any[]) {
        let query = `INSERT into ${tableName} ( `;
        query += columns.map((col) => `'${col}'`).join(`,`);
        query += `) `;

        query +=
            `VALUES ` +
            data.map((record) => {
                let recordQuery = `(`;
                recordQuery += Object.values(record)
                    .map((val) => {
                        if (typeof val === 'string') {
                            return `'${val.replace(/'/g, "''")}'`;
                        } else {
                            return `${val}`;
                        }
                    })
                    .join(`,`);
                return (recordQuery += `) `);
            });
        return query;
    }

    async #createVisitDB(visit: DownSyncResponseDto, deleteIfFailed = false) {
        const connected: boolean = await this.#sqliteSvc.connectDB(visit.visitNo + '.db', 'sync', true);
        if (!connected) {
            throw new GenericError({
                message: 'Failed to connect to database',
                errorCode: ErrorCode.DB_CONNECTION_FAILED,
            });
        }

        for await (const table of visit.tables) {
            // run create tabel statement
            await this.#sqliteSvc.runQuery<void>(table.ddl.replace(/\n/g, ''), 'sync').catch(async (err) => {
                if (deleteIfFailed) {
                    await this.#sqliteSvc.deleteDB(visit.visitNo + '.db', 'sync', true);
                }
                throw err;
            });

            const data = table.data;
            if (!data) {
                continue;
            }
            const columns: string[] = Object.keys(data[0]);
            const tableName: string = table.tableName;

            // run insert data statement
            const insertQuery: string = this.#createInsertQuery(tableName, columns, data);

            await this.#sqliteSvc.runQuery<void>(insertQuery, 'sync');
        }
    }

    async #getAttDownSyncUrls(visitNo: string, targetLocation: PerformAuditAttachmentLocation, auditNo?: string) {
        let attQuery: string = DownSyncAttPayloadQuery(visitNo, targetLocation, auditNo)

        const data = await this.#sqliteSvc.runQuery<AttDownloadUrlReqDto[]>(attQuery, 'sync');
        if (!data.length) {
            return [];
        }
        const url = Environment.baseUrl + PerformUrl.attDownloadUrl;
        const response = await this.#httpSvc.post<AjaxResponse<AttDownloadUrlResDto[]>>([url, data], true);
        const urls: AttDownloadUrlResDto[] = response.data;
        return urls;
    }

    async #downloadAttFiles(urls: AttDownloadUrlResDto[], visitNo: string, targetLocation: PerformAuditAttachmentLocation): Promise<void>;
    async #downloadAttFiles(urls: AttDownloadUrlResDto[], visitNo: string, targetLocation: PerformAuditAttachmentLocation, audit_no: string): Promise<void>;
    async #downloadAttFiles(urls: AttDownloadUrlResDto[], visitNo: string, targetLocation: PerformAuditAttachmentLocation, audit_no?: string): Promise<void> {
        const batchSize = 5;
        const loopCount = Math.ceil(urls.length / batchSize);
        const failedDownAtt: GenericError<any>[] = []
        let startIndex = 0;
        for (let i = 0; i < loopCount; i++) {
            const batch = urls.slice(startIndex, startIndex + batchSize);
            const filesMap = new Map<string, File>();
            const filesDownloaded = new Map<string, AttachmentCloutUploadStatus>();
            for await (const url of batch) {
                try {
                    const response = await fetch(url.downloadUrl);
                    if (!response.ok) {
                        throw new HttpError(
                            `Attachment download failed. Response status : ${response.status}`,
                            ErrorCode.ATT_DOWN_SYNC_FAILED,
                            response.status,
                            url.downloadUrl
                        )
                    }
                    const blob = await response.blob();
                    const file = new File([blob], url.attachmentName, { type: blob.type });
                    filesMap.set(file.name, file);
                    filesDownloaded.set(url.seq, AttachmentCloutUploadStatus.Downloaded);
                } catch (err) {
                    const log: Log = {
                        date: new Date().toISOString(),
                        msg: 'download attachment failed',
                        object: err,
                        type: 'error'
                    };
                    filesDownloaded.set(url.seq, AttachmentCloutUploadStatus.DownloadedFailed);
                    failedDownAtt.push(new GenericError({
                        errorCode: ErrorCode.ATT_DOWN_SYNC_FAILED,
                        message: 'attachment download failed',
                        location: 'perform-sync.service>downloadAttFiles()',
                        errorObject: err
                    }));
                    this.#loggerSvc.add(log);
                }
            }
            if (audit_no) {
                await this.#fileSystemSvc.saveAttachments(filesMap, visitNo, targetLocation, audit_no);
            } else {
                await this.#fileSystemSvc.saveAttachments(filesMap, visitNo, targetLocation);
            }

            startIndex += batchSize;
        }
        if (failedDownAtt.length > 0) {
            throw new GenericError({
                errorCode: ErrorCode.ATT_DOWN_SYNC_FAILED,
                message: 'One or more attachments download failed',
                errorObject: failedDownAtt,
                location: 'perform-sync.service>downloadAttFiles()'
            })
        }
    }

    async #insertAttDownSyncUrlInDB(urls: AttDownloadUrlResDto[]) {
        const updateQuery = updateDownSyncAtt(urls);
        await this.#sqliteSvc.runQuery(updateQuery, 'sync');
    }

    async #downSyncAttachments(visitNo: string) {
        const auditQuery = GET_AUDIT_LIST(visitNo);
        const failedDownAtt: GenericError<any>[] = []
        const auditList = await this.#sqliteSvc.runQuery<{ audit_no: string }[]>(auditQuery, 'sync');
        const attachmentLocations: PerformAuditAttachmentLocation[] = [
            PerformAuditAttachmentLocation.Audit,
            PerformAuditAttachmentLocation.Attempted,
            PerformAuditAttachmentLocation.Questions
        ]
        for await (const audit of auditList) {
            for await (const location of attachmentLocations) {
                const urls: AttDownloadUrlResDto[] = await this.#getAttDownSyncUrls(visitNo, location, audit.audit_no);
                if (!urls.length) {
                    continue;
                }
                await this.#insertAttDownSyncUrlInDB(urls);
                await this.#downloadAttFiles(urls, visitNo, location, audit.audit_no).catch(err => {
                    if (err instanceof GenericError && err.errorCode === ErrorCode.ATT_DOWN_SYNC_FAILED) {
                        (err.errorObject as GenericError<any>[]).forEach((_err) => {
                            failedDownAtt.push(_err)
                        })
                    } else {
                        throw err;
                    }
                })
            }
        }

        // download attachments of signature
        const urls: AttDownloadUrlResDto[] = await this.#getAttDownSyncUrls(visitNo, PerformAuditAttachmentLocation.SubmitFormSignature);
        if (!urls.length) {
            return;
        }
        await this.#insertAttDownSyncUrlInDB(urls);
        await this.#downloadAttFiles(urls, visitNo, PerformAuditAttachmentLocation.SubmitFormSignature).catch(err => {
            if (err instanceof GenericError && err.errorCode === ErrorCode.ATT_DOWN_SYNC_FAILED) {
                (err.errorObject as GenericError<any>[]).forEach((_err) => {
                    failedDownAtt.push(_err)
                })
            } else {
                throw err;
            }
        })

        if (failedDownAtt.length > 0) {
            throw new GenericError({
                errorCode: ErrorCode.ATT_DOWN_SYNC_FAILED,
                message: 'One or more attachments download failed',
                errorObject: failedDownAtt,
                location: 'perform-sync.service>downloadAttFiles()'
            })
        }

    }

    async #saveupSyncInfo(visitInfo: VisitUpSyncInfoTable[]) {
        await this.#performIdbSvc.addUpSyncVisitInfo(visitInfo);
    }

    async startDownSync(visitNo: string[], deleteIfFailed = false) {
        try {
            const userName = this.#msalAuthSvc.activeUserName;
            let syncErrors: ErrorBase[] = [];
            if (!userName) {
                throw new GenericError({
                    message: 'userName not found',
                    errorCode: ErrorCode.NULL_OR_EMPTY,
                    location: 'perform-sync-.service>autoSync()',
                });
            }
            await this.#updateLastSyncTime(visitNo, userName, VisitDbSyncTaskStatus.InDownLoadQueue);
            for (const _visitNo of visitNo) {
                try {
                    const visitDbExists: boolean = await this.#sqliteSvc.dbExists(_visitNo + '.db');
                    if (visitDbExists) {
                        const log: Log = {
                            date: new Date().toISOString(),
                            msg: `visit ${_visitNo} db already exists. not downloading again.`,
                            object: _visitNo,
                            type: 'info',
                        }
                        await this.#loggerSvc.add(log);
                        await this.#updateLastSyncTime(_visitNo, userName, VisitDbSyncTaskStatus.Downloaded);
                        continue;
                    }
                    await this.#updateLastSyncTime(_visitNo, userName, VisitDbSyncTaskStatus.Downloading);
                    const dbName = _visitNo + '.db';
                    // await this.#sqliteSvc.deleteDB(dbName, 'sync', true);
                    const response = await this.downloadVisitTables(_visitNo);
                    await this.#createVisitDB(response, deleteIfFailed);
                    await this.#sqliteSvc.connectDB(dbName, 'sync', false);
                    // this.#loaderSvc.show(this.#translateSvc.instant('downAttTxt'));
                    await this.#downSyncAttachments(_visitNo);

                    await this.#updateLastSyncTime(_visitNo, userName, VisitDbSyncTaskStatus.Downloaded);
                } catch (err) {
                    this.#loggerSvc.add({
                        msg: 'error in PerformSyncService>startDownSync()',
                        object: err,
                        date: new Date().toISOString(),
                        type: 'error',
                    });

                    syncErrors.push(err as ErrorBase)
                    const errMsg = this.#utilSvc.getErrorMessage(err);
                    this.#sqliteSvc.deleteDB(_visitNo + '.db', 'sync', true);
                    this.#fileSystemSvc.clearVisitAttachments(_visitNo);
                    await this.#updateLastSyncTime(_visitNo, userName, VisitDbSyncTaskStatus.DownloadFailed, errMsg);
                }
            }

            if (syncErrors.length > 0) {
                throw new GenericError({
                    errorCode: ErrorCode.SYNC_FAILED,
                    message: 'One or more visit failed in down sync ',
                    errorObject: syncErrors
                })
            }

        } finally {
            this.#sqliteSvc.closeConnection('sync');
        }
    }

    async deleteVisitsAndAttachments(visits: string[], userName: string, preserveIndexDbData: boolean = false) {
        for await (let visit of visits) {
            await this.#fileSystemSvc.clearVisitAttachments(visit);
            await this.#sqliteSvc.deleteDB(visit + '.db', 'sync', true);
            if (preserveIndexDbData) {
                continue;
            }
            await this.#performIdbSvc.deleteUpSyncVisitInfo(visit, userName)
            await this.#performIdbSvc.deleteBackboneVisit(visit + userName)
        }
    }

    //#endregion
    //#region auto sync
    async startSync(options?: {
        syncType?: 'full-up-and-down' | 'single-up-and-down' | 'up-sync-only',
        visitNos?: string[]
    }): Promise<PerformSyncResult> {
        try {
            if ((this.#storageSvc.getSessionData(SYNC_IN_PROGRESS) == 'true')) {
                return PerformSyncResult.In_Progress;
            }
            this.#storageSvc.setSessionData(SYNC_IN_PROGRESS, 'true');
            const userName = this.#msalAuthSvc.activeUserName;
            if (!userName) {
                throw new GenericError({
                    message: 'userName not found',
                    errorCode: ErrorCode.NULL_OR_EMPTY,
                    location: 'perform-sync-.service>autoSync()',
                });
            }
            const getFailedVisits = async () => await this.#performIdbSvc.getSyncFailedVisits(userName);
            const visits: string[] = options?.visitNos ? options.visitNos : (await this.#performIdbSvc.getBackboneVisits(userName)).map(v => v.visit_no);
            for await (let visitNo of visits) {
                try {
                    this.#updateLastSyncTime(visitNo, userName, VisitDbSyncTaskStatus.Uploading);
                    await this.#startUpSync(visitNo)
                    const status = await this.#performIdbSvc.getVisitStatus(visitNo, userName);
                    if(status === VisitStatus.SUBMITTED) {
                        const preserveIndexDbData = false;
                        await this.deleteVisitsAndAttachments([visitNo], userName, preserveIndexDbData);
                    } else {
                        this.#updateLastSyncTime(visitNo, userName, VisitDbSyncTaskStatus.Uploaded);
                    }
                }
                catch (err) {
                    this.#updateLastSyncTime(visitNo, userName, VisitDbSyncTaskStatus.UploadFailed);
                    await this.#loggerSvc.add({
                        msg: 'error in PerformSyncService>startAutoSync()',
                        object: err,
                        date: new Date().toISOString(),
                        type: 'error',
                    });
                }
            }

            if ((await getFailedVisits()).length > 0) {
                this.#storageSvc.removeSession(SYNC_IN_PROGRESS);
                return PerformSyncResult.Failed;
            }
            if (options?.syncType == 'full-up-and-down' || options?.syncType == 'single-up-and-down') {

                const preserveIndexDbData = options?.syncType === 'single-up-and-down';
                await this.deleteVisitsAndAttachments(visits, userName, preserveIndexDbData);
                let visitsToDownSync: string[];
                if (options?.visitNos) {
                    visitsToDownSync = options?.visitNos;
                } else {

                    const freshVisits = await this.#visitListSvc.fetchNewBackboneVisits();
                    if (freshVisits.created.length == 0) {
                        return PerformSyncResult.Success;
                    }
                    visitsToDownSync = freshVisits.created.map(v => v.visit_no);
                }
                await this.startDownSync(visitsToDownSync, true);
                if ((await getFailedVisits()).length > 0) {
                    this.#storageSvc.removeSession(SYNC_IN_PROGRESS);
                    return PerformSyncResult.Failed;
                }
            }

            this.#storageSvc.removeSession(SYNC_IN_PROGRESS);
            return PerformSyncResult.Success;
        } catch (err) {
            this.#storageSvc.removeSession(SYNC_IN_PROGRESS);
            if (options?.syncType === 'single-up-and-down') {
                if (err instanceof ErrorBase) {
                    if (err.errorObject[0] instanceof ErrorBase) {
                        throw err.errorObject[0];
                    }
                }
            }
            throw err;
        }
    }
    async startAutoDownSync(deleteIfFailed: boolean = true): Promise<PerformSyncResult> {
        try {

            if ((this.#storageSvc.getSessionData(SYNC_IN_PROGRESS) == 'true')) {
                return PerformSyncResult.In_Progress;
            }
            this.#storageSvc.setSessionData(SYNC_IN_PROGRESS, 'true');
            const userName = this.#msalAuthSvc.activeUserName;
            if (!userName) {
                throw new GenericError({
                    message: 'userName not found',
                    errorCode: ErrorCode.NULL_OR_EMPTY,
                    location: 'perform-sync-.service>autoSync()',
                });
            }
            const visits = await this.#performIdbSvc.getBackboneVisits(userName, {
                exclude: [
                    VisitStatus.COMPLETED,
                    VisitStatus.SUBMITTED,
                    VisitDbSyncTaskStatus.Downloaded,
                ]
            });
            const visitsNos: string[] = visits.map((visit) => visit.visit_no);
            await this.startDownSync(visitsNos, deleteIfFailed);

            this.#storageSvc.setSessionData(SYNC_IN_PROGRESS, 'false');
            return PerformSyncResult.Success;
        } catch (err) {
            this.#loggerSvc.add({
                msg: 'error in PerformSyncService>startAutoSync()',
                object: err,
                date: new Date().toISOString(),
                type: 'error',
            });
            this.#storageSvc.setSessionData(SYNC_IN_PROGRESS, 'false');
            return PerformSyncResult.Failed;
        }
    }



    //#endregion


    //#region sync geocode and contacts

    async downSyncGeoCode(visitNo: string) {
        try {
            // await this.#upSyncVisit(visitNo, ['geocode']);
            const tableResponse: DownSyncResponseDto = await this.downloadVisitTables(visitNo, ['geocode'])
            const geocodeTableData = tableResponse?.tables.find((table) => table.tableName === 'geocode');
            if (geocodeTableData?.data && geocodeTableData.data.length > 0) {
                const newGeoCode = geocodeTableData.data[0]
                if (newGeoCode) {
                    const updateSeqQuery = updateGeocodeSeq(newGeoCode.seq)
                    await this.#sqliteSvc.runQuery(updateSeqQuery, 'sync')
                }
            } else {
                const log: Log = {
                    date: new Date().toISOString(),
                    msg: 'no data found in geocode table at down sync',
                    object: geocodeTableData,
                    type: 'info'
                }
                this.#loggerSvc.add(log);
            }
            if (await this.#appSettingSvc.exists(this.#getDownSyncAppSettingKey(GEOCODE_FAILED_VISIT, visitNo))) {
                this.#appSettingSvc.delete(this.#getDownSyncAppSettingKey(GEOCODE_FAILED_VISIT, visitNo))
            }
        } catch (err) {
            this.#loggerSvc.add({
                msg: 'error in PerformSyncService>downSyncGeoCode()',
                object: err,
                date: new Date().toISOString(),
                type: 'error',
            });
            this.#appSettingSvc.setString(this.#getDownSyncAppSettingKey(GEOCODE_FAILED_VISIT, visitNo), 'true')
            throw err;
        }
    }

    async downSyncContacts(visitNo: string) {
        try {

            const newContacts = await this.#sqliteSvc.runQuery<PerformContact[]>(getNullSeqContacts());
            if (newContacts.length == 0) {
                return;
            }
            let newContactWithSeq = []
            const tableResponse: DownSyncResponseDto = await this.downloadVisitTables(visitNo, ['contacts'])
            const contactTableData = tableResponse?.tables.find((table) => table.tableName === 'contacts');
            if (!contactTableData?.data) {
                const log: Log = {
                    date: new Date().toISOString(),
                    msg: 'no data found in contacts table at down sync',
                    object: contactTableData?.data,
                    type: 'info'
                }
                this.#loggerSvc.add(log);
                return
            }
            newContactWithSeq = contactTableData.data.filter((contact: PerformContact) => {
                const newContactId = contact.email + contact.last_name;
                return newContacts.some((newContact: PerformContact) => {
                    return newContact.email + newContact.last_name === newContactId;
                });
            })
            const updateSeqQuery = updateContactSqs(newContactWithSeq)
            await this.#sqliteSvc.runQuery(updateSeqQuery, 'sync')
            if (await this.#appSettingSvc.exists(this.#getDownSyncAppSettingKey(CONTACTS_FAILED_VISIT, visitNo))) {
                this.#appSettingSvc.delete(this.#getDownSyncAppSettingKey(CONTACTS_FAILED_VISIT, visitNo))
            }
        }
        catch (err) {
            this.#loggerSvc.add({
                msg: 'error in PerformSyncService>downSyncContacts()',
                object: err,
                date: new Date().toISOString(),
                type: 'error',
            });
            this.#appSettingSvc.setString(this.#getDownSyncAppSettingKey(CONTACTS_FAILED_VISIT, visitNo), 'true')
            throw err;
        }
    }



    //#endregion


}
